'use client';

import React, { useEffect, useRef } from 'react';

export default function ProblemSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-reveal, .scroll-reveal-left, .scroll-reveal-right').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="problem"
      ref={sectionRef}
      className="py-32 lg:py-40"
      style={{ background: 'linear-gradient(180deg, #061409 0%, #0A1F0F 100%)' }}
      aria-labelledby="problem-heading"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Label */}
        <div className="scroll-reveal mb-6">
          <span className="section-label">01 / The Problem</span>
        </div>

        {/* Headline */}
        <div className="scroll-reveal mb-16" style={{ transitionDelay: '100ms' }}>
          <h2 id="problem-heading" className="font-display text-[clamp(2.5rem,6vw,5rem)] leading-[0.9] tracking-[-0.04em] text-white max-w-4xl">
            This is not a supply issue.{' '}
            <span className="text-white/70">It is a structural</span>{' '}
            <span className="text-yellow-400">food security failure.</span>
          </h2>
        </div>

        {/* Main deficit visual */}
        <div className="scroll-reveal mb-20" style={{ transitionDelay: '200ms' }}>
          <div className="glass-dark rounded-3xl p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-0 items-center">
              {/* Demand */}
              <div className="text-center lg:text-left">
                <p className="section-label mb-3">Annual Demand</p>
                <div className="font-display text-[clamp(3.5rem,8vw,6rem)] text-white leading-none tracking-[-0.05em]">
                  9B
                </div>
                <p className="text-white/70 text-sm mt-2 font-medium">eggs per year</p>
              </div>

              {/* Gap visual */}
              <div className="flex flex-col items-center gap-4">
                <div className="w-full max-w-xs">
                  <div className="flex justify-between text-xs text-white/70 mb-2 font-mono-custom">
                    <span>Supply</span>
                    <span>73%</span>
                  </div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full progress-bar"
                      style={{ width: '73%', background: 'linear-gradient(90deg, #16A34A, #22C55E)' }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-white/70 mt-3 mb-2 font-mono-custom">
                    <span>Deficit</span>
                    <span>27%</span>
                  </div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full progress-bar"
                      style={{ width: '27%', background: 'linear-gradient(90deg, #FACC15, #FDE047)', animationDelay: '0.3s' }}
                    />
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-xs text-white/25 font-mono-custom uppercase tracking-widest">Supply Gap</p>
                  <p className="font-display text-4xl text-yellow-400 tracking-[-0.04em]">2.4B</p>
                  <p className="text-xs text-white/70">eggs/year unmet</p>
                </div>
              </div>

              {/* Supply */}
              <div className="text-center lg:text-right">
                <p className="section-label mb-3">Current Supply</p>
                <div className="font-display text-[clamp(3.5rem,8vw,6rem)] text-white leading-none tracking-[-0.05em]">
                  6.6B
                </div>
                <p className="text-white/70 text-sm mt-2 font-medium">eggs produced</p>
              </div>
            </div>
          </div>
        </div>

        {/* Problem cards — asymmetric bento */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 stagger-children">
          {/* Card 1 — wide */}
          <div className="scroll-reveal lg:col-span-5 glass-dark rounded-2xl p-7 hover-lift">
            <div className="w-10 h-10 rounded-xl bg-yellow-400/10 flex items-center justify-center mb-5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FACC15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" />
              </svg>
            </div>
            <h3 className="font-display text-xl text-white mb-3 tracking-tight">Per Capita Consumption Gap</h3>
            <p className="text-white/45 text-sm leading-relaxed mb-5">
              Kenya averages 36 eggs per person annually — against the recommended 180. A 5× consumption gap waiting to be closed.
            </p>
            <div className="flex items-end gap-3">
              <div className="text-center">
                <div className="font-display text-3xl text-yellow-400">36</div>
                <p className="text-xs text-white/70 font-mono-custom">Current</p>
              </div>
              <div className="flex-1 h-px bg-white/10 mb-3" />
              <div className="text-center">
                <div className="font-display text-3xl text-white">180</div>
                <p className="text-xs text-white/70 font-mono-custom">Recommended</p>
              </div>
            </div>
          </div>

          {/* Card 2 — tall */}
          <div className="scroll-reveal lg:col-span-4 glass-green rounded-2xl p-7 hover-lift">
            <div className="w-10 h-10 rounded-xl bg-green-primary/20 flex items-center justify-center mb-5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10" /><path d="m4.9 4.9 14.2 14.2" />
              </svg>
            </div>
            <h3 className="font-display text-xl text-white mb-3 tracking-tight">Import Dependency</h3>
            <p className="text-white/45 text-sm leading-relaxed mb-6">
              Kenya spends over $58M annually importing eggs — capital that leaves the economy while domestic capacity remains structurally underdeveloped.
            </p>
            <div className="glass-dark rounded-xl p-4 text-center">
              <div className="font-display text-4xl text-yellow-400">$58M+</div>
              <p className="text-xs text-white/70 font-mono-custom mt-1">Annual import spend</p>
            </div>
          </div>

          {/* Card 3 — narrow */}
          <div className="scroll-reveal lg:col-span-3 glass-dark rounded-2xl p-7 hover-lift flex flex-col">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center mb-5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="opacity-60">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3 className="font-display text-xl text-white mb-3 tracking-tight">Price Volatility</h3>
            <p className="text-white/45 text-sm leading-relaxed flex-1">
              Supply chain fragility creates unpredictable price swings, undermining food security for 54M Kenyans.
            </p>
            <div className="mt-6 space-y-2">
              {['Supply chain fragility', 'Quality inconsistency', 'Retailer margin pressure']?.map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-yellow-400/60 flex-shrink-0" />
                  <span className="text-xs text-white/70">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom statement */}
        <div className="scroll-reveal mt-16 text-center" style={{ transitionDelay: '400ms' }}>
          <p className="font-display text-2xl lg:text-3xl text-white/20 tracking-tight max-w-2xl mx-auto leading-tight">
            "Demand is structurally guaranteed.{' '}
            <span className="text-white/60">Supply is structurally broken."</span>
          </p>
        </div>
      </div>
    </section>
  );
}